// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@hardwoods/App/Components/Notify";
import PaidInvoiceFilter from "@hardwoods/App/Components/PaidInvoices/Filter";
import loadPaidInvoices from "@hardwoods/App/Handlers/PaidInvoices/loadPaidInvoices";
import { paidInvoicesModel } from "@hardwoods/App/Services/PaidInvoicesService";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { invoicePaymentStyle } from "@hardwoods/App/Widgets/InvoicePayment/InvoicePayment.style";
import { capitalize } from "@hardwoods/XDL/Utilities";
import { isAuthenticated } from "@hardwoods/XDL/Utilities";
import { areObjectValuesNotEmpty } from "@hardwoods/XDL/Utilities/helper";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Filter from "@insite/mobius/Icons/Filter";
import { Clickable, Icon, LoadingOverlay, Typography } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface PaidInvoicesFilters {
    paymentDate: string[];
    paidBy: string[];
    paymentMethod: string[];
    invoiceNumber: string[];
    custPoNumber: string[];
    invoiceDate: string[];
    dueDate: string[];
    amountDue: string[];
    amountPaid: string[];
}

export type FilterName = keyof PaidInvoicesFilters;

export type ChangeFilterSearchHandler = (event: React.ChangeEvent<HTMLInputElement>, key: FilterName) => void;

export type ChangeFilterHandler = (
  _: React.SyntheticEvent<Element, Event>,
  value: boolean,
  filterName: FilterName,
  filterValue: string
) => void;

export type ChangeAllSelectFilterHandler = (
  _: React.SyntheticEvent<Element, Event>,
  value: boolean,
  filterName: FilterName
) => void;

export type IsFilterChecked = (filterName: FilterName, filterValue: string) => boolean;

export type IsAllFiltersChecked = (filterName: FilterName) => boolean;

export type GetFilters = <K extends keyof PaidInvoicesFilters>(columnName: K) => string[];

export type ToggleFilter = (event: React.MouseEvent, columnName: string) => void;

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const settings: any = getSettingsCollection(state);
    const session = getSession(state);

    return {
        invoices: state.xPaidInvoices.invoices,
        isLoading: state.xPaidInvoices.isLoading,
        settings,
        billTo: getBillToState(state, session.billToId).value,
        pageTitle: getCurrentPage(state).fields.title,
        isAuthenticated: isAuthenticated(session),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    loadPaidInvoices,
};

const style = invoicePaymentStyle;

const PaidInvoicesWidget: FC<Props> = ({
    invoices,
    isLoading,
    billTo,
    pageTitle,
    isAuthenticated,
    loadPaidInvoices,
}) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [filteredInvoices, setFilteredInvoices] = useState<paidInvoicesModel[]>([]);
    const [openedFilter, setOpenedFilter] = useState("");
    const initalPaidInvoicesFilter = {
        customerNumber: [],
        erpNumber: [],
        paymentDate: [],
        invoiceDate: [],
        dueDate: [],
        paidBy: [],
        paymentMethod: [],
        invoiceNumber: [],
        custPoNumber: [],
        amountDue: [],
        amountPaid: [],
    };
    const [invoicesFilters, setInvoicesFilters] = useState<PaidInvoicesFilters>(initalPaidInvoicesFilter);
    const [selectedInvoicesFilters, setSelectedInvoicesFilters] = useState<PaidInvoicesFilters>(initalPaidInvoicesFilter);
    const [unSelectedInvoicesFilters, setUnSelectedInvoicesFilters] = useState<PaidInvoicesFilters>(initalPaidInvoicesFilter);
    const [filterSearch, setFilterSearch] = useState<{ [key: string]: any }>({});

    // XNG-Changes: showing amount paid and amount due display
    useEffect(() => {
        if (invoices) {
            const tempInvoices = invoices?.filter((invoice: paidInvoicesModel) => {
                return (
                    selectedInvoicesFilters.paymentDate.includes(invoice.paymentDate) &&
                    selectedInvoicesFilters.paidBy.includes(invoice.paidBy) &&
                    selectedInvoicesFilters.paymentMethod.includes(invoice.paymentMethod) &&
                    selectedInvoicesFilters.invoiceNumber.includes(invoice.invoiceNumber) &&
                    selectedInvoicesFilters.custPoNumber.includes(invoice.custPoNumber) &&
                    selectedInvoicesFilters.invoiceDate.includes(invoice.invoiceDate) &&
                    selectedInvoicesFilters.dueDate.includes(invoice.dueDate) &&
                    (selectedInvoicesFilters.amountDue.includes(`${invoice.amountDue}`) ||
                        selectedInvoicesFilters.amountDue.includes(invoice.amountDueDisplay)) &&
                    (selectedInvoicesFilters.amountPaid.includes(`${invoice.amountPaid}`) ||
                        selectedInvoicesFilters.amountPaid.includes(invoice.amountPaidDisplay))
                );
            });
            setFilteredInvoices(tempInvoices);
        }
    },[selectedInvoicesFilters]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        if(!invoices?.length && !isLoading) {
            loadPaidInvoices({
                onError: errorMessage => {
                    setErrorMessage(errorMessage);
                },
            });
        }

        setFilterSearch({
            customerNumber: "",
            erpNumber: "",
            paymentDate: "",
            invoiceDate: "",
            dueDate: "",
            paidBy: "",
            paymentMethod: "",
            invoiceNumber: "",
            custPoNumber: "",
            amountDue: "",
            amountPaid: "",
        });
    }, []);

    useEffect(() => {
        if (invoices) {
            setFilteredInvoices(invoices);
            setInvoicesFilters({
                paymentDate: prepareFilters("paymentDate"),
                paidBy: prepareFilters("paidBy"),
                paymentMethod: prepareFilters("paymentMethod"),
                invoiceNumber: prepareFilters("invoiceNumber"),
                custPoNumber: prepareFilters("custPoNumber"),
                invoiceDate: prepareFilters("invoiceDate"),
                dueDate: prepareFilters("dueDate"),
                amountDue: prepareFilters("amountDue"),
                amountPaid: prepareFilters("amountPaid"),
            });
            // In some cases invoicesFilters will not be ready to read from state
            // we have to prepare filters not reading from state
            setSelectedInvoicesFilters({
                paymentDate: prepareFilters("paymentDate"),
                paidBy: prepareFilters("paidBy"),
                paymentMethod: prepareFilters("paymentMethod"),
                invoiceNumber: prepareFilters("invoiceNumber"),
                custPoNumber: prepareFilters("custPoNumber"),
                invoiceDate: prepareFilters("invoiceDate"),
                dueDate: prepareFilters("dueDate"),
                amountDue: prepareFilters("amountDue"),
                amountPaid: prepareFilters("amountPaid"),
            });
        }
    }, [invoices]);

    if (!isAuthenticated) {
        return (
            <StyledWrapper data-test-selector="PaidInvoices_loading" {...style.notifyMessage.wrapper}>
                <Notify messageType="danger" body={siteMessage("PaidInvoices_SignIn", "You must SignIn")} />
            </StyledWrapper>
        );
    }

    const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
    
        // Check if the clicked element or any of its ancestors have the class "invoice-filter"
        if (!target.closest('.invoice-filter')) {
            setOpenedFilter("");
            document.removeEventListener("click", handleDocumentClick);
            setUnSelectedInvoicesFilters(initalPaidInvoicesFilter);
        }
    };

    const toggleFilter = (event: React.MouseEvent, columnName: string) => {
        event.stopPropagation();
        if(openedFilter === columnName) {
            setOpenedFilter("");
            setUnSelectedInvoicesFilters(initalPaidInvoicesFilter);
        } else {
            setOpenedFilter(columnName);
            document.addEventListener("click", handleDocumentClick);    
        }
    };

    const isOpenedFilter = (columnName: string) => {
        return openedFilter === columnName;
    };
    
    const prepareFilters = <K extends keyof paidInvoicesModel>(columnName: K): string[] => {
        const filteredValues = invoices.map((invoice: paidInvoicesModel) => {
            const value = invoice[columnName];
            if (columnName === "amountPaid") {
                return invoice.amountPaidDisplay; // Use amountPaidDisplay for amountPaid filter
            }
            if (columnName === "amountDue") {
                return invoice.amountDueDisplay; // Use amountDueDisplay for amountDue filter
            }
            return value !== null ? `${value}` : "blank";
        });

        // Ensure "blank" appears only once in the array
        const uniqueFilteredValues = Array.from(new Set<string>(filteredValues || []));
    
        // Sorting filters because we can't compare un-sorted filters
        switch (columnName) {
            case 'invoiceNumber':
                uniqueFilteredValues.sort((a, b) => a.localeCompare(b, 'en', { numeric: true })); // Sort alpha-numerically
                break;
            case 'paymentDate':
            case 'invoiceDate':
            case 'dueDate':
                uniqueFilteredValues.sort((a, b) => {
                    // If either value is 'blank', treat it as the smallest value
                    if (a === 'blank' || b === 'blank') {
                        if (a === 'blank' && b === 'blank') {return 0;}
                        if (a === 'blank') {return -1;}
                        return 1;
                    }
                    // Convert the values to dates for comparison
                    const dateA = new Date(a).getTime();
                    const dateB = new Date(b).getTime();
                    return dateA - dateB;
                });
                break;
            case 'amountDue':
            case 'amountPaid':
                uniqueFilteredValues.sort((a, b) => parseFloat(a.replace(/[^\d.-]/g, '')) - parseFloat(b.replace(/[^\d.-]/g, ''))); // Sort by numbers ascending
                break;
            case 'paidBy':
            case 'paymentMethod':
            case 'custPoNumber':
                uniqueFilteredValues.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })); // Sort alphabetically A-Z
                break;
            default:
        }
        return uniqueFilteredValues;
    };

    const getFilters = <K extends keyof PaidInvoicesFilters>(columnName: K): string[] => {
        const currentInvoices = filteredInvoices;

        // Map the invoices to get the values for the specified column
        const filters = currentInvoices.map(invoice => {
            const value = invoice[columnName];
            if (columnName === "amountPaid") {
                return invoice.amountPaidDisplay; // Use amountPaidDisplay for amountPaid filter
            }
            if (columnName === "amountDue") {
                return invoice.amountDueDisplay; // Use amountDueDisplay for amountDue filter
            }
            return `${value === 0 ? 0 : value || "blank"}`; // Use 'blank' if the value is null
        });

        // Include the unchecked filters from local state
        const uncheckedFilters = unSelectedInvoicesFilters[columnName];
        
        if (uncheckedFilters && uncheckedFilters.length > 0) {
            uncheckedFilters.forEach(filter => {
                const index = filters.indexOf(filter);
                if (index === -1) {
                    filters.push(filter);
                }
            });
        }

        // Remove duplicate values and sort them as needed
        const uniqueFilters = Array.from(new Set(filters));

        switch (columnName) {
            case 'invoiceNumber':
                uniqueFilters.sort((a, b) => a.localeCompare(b, 'en', { numeric: true })); // Sort alpha-numerically
                break;
            case 'paymentDate':
            case 'invoiceDate':
            case 'dueDate':
                uniqueFilters.sort((a, b) => {
                    // If either value is 'blank', treat it as the smallest value
                    if (a === 'blank' || b === 'blank') {
                        if (a === 'blank' && b === 'blank') {return 0;}
                        if (a === 'blank') {return -1;}
                        return 1;
                    }
                    // Convert the values to dates for comparison
                    const dateA = new Date(a).getTime();
                    const dateB = new Date(b).getTime();
                    return dateA - dateB;
                });
                break;
            case 'amountDue':
            case 'amountPaid':
                uniqueFilters.sort((a, b) => parseFloat(a.replace(/[^\d.-]/g, '')) - parseFloat(b.replace(/[^\d.-]/g, ''))); // Sort by numbers ascending
                break;
            case 'paidBy':
            case 'paymentMethod':
            case 'custPoNumber':
                uniqueFilters.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })); // Sort alphabetically A-Z
                break;
            default:
        }
        
        return uniqueFilters.filter((filter: string) =>
            filter.toLowerCase().includes(filterSearch[columnName]?.toLowerCase())
        );
    };

    const onChangeFilterSearch = (event: React.ChangeEvent<HTMLInputElement>, key: FilterName) => {
        const searchTerm = event.target.value.toLowerCase();
        setFilterSearch({ ...filterSearch, [key]: searchTerm });
        const filters = prepareFilters(key).filter((filter: string) =>
            filter.toLowerCase().includes(searchTerm)
        );
        // Update the selected filters based on the filtered list of options
        setSelectedInvoicesFilters(prevState => {
            return { ...prevState, [key]: filters };
        });
    };

    const onChangeFilterHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean, filterName: FilterName, filterValue: string) => {
        setSelectedInvoicesFilters(prevState => {
            const updatedFilters = [...prevState[filterName]];
            const filterIndex = updatedFilters.indexOf(filterValue);
    
            if (filterIndex > -1) {
                updatedFilters.splice(filterIndex, 1);
            } else {
                updatedFilters.push(filterValue);
            }

            switch (filterName) {
                case 'invoiceNumber':
                    updatedFilters.sort((a, b) => a.localeCompare(b, 'en', { numeric: true })); // Sort alpha-numerically
                    break;
                case 'paymentDate':
                case 'invoiceDate':
                case 'dueDate':
                    updatedFilters.sort((a, b) => {
                        // If either value is 'blank', treat it as the smallest value
                        if (a === 'blank' || b === 'blank') {
                            if (a === 'blank' && b === 'blank') {return 0;}
                            if (a === 'blank') {return -1;}
                            return 1;
                        }
                        // Convert the values to dates for comparison
                        const dateA = new Date(a).getTime();
                        const dateB = new Date(b).getTime();
                        return dateA - dateB;
                    });
                    break;
                case 'amountDue':
                case 'amountPaid':
                    updatedFilters.sort((a, b) => parseFloat(a.replace(/[^\d.-]/g, '')) - parseFloat(b.replace(/[^\d.-]/g, ''))); // Sort by balance ascending
                    break;
                case 'paidBy':
                case 'paymentMethod':
                case 'custPoNumber':
                    updatedFilters.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })); // Sort alphabetically A-Z
                    break;
                default:
            }
    
            return {
                ...prevState,
                [filterName]: updatedFilters,
            };
        });
        setUnSelectedInvoicesFilters(prevState => {
            const updatedFilters = [...prevState[filterName]];
            const filterIndex = updatedFilters.indexOf(filterValue);
    
            if (filterIndex > -1) {
                updatedFilters.splice(filterIndex, 1);
            } else {
                updatedFilters.push(filterValue);
            }
    
            return {
                ...prevState,
                [filterName]: updatedFilters,
            };
        });
    };

    const onChangeAllSelectFilterHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean, filterName: FilterName) => {
        setFilterSearch({ ...filterSearch, [filterName]: "" });
        setSelectedInvoicesFilters(prevState => ({
            ...prevState,
            [filterName]: value ? [] : invoicesFilters[filterName],
        }));
        setUnSelectedInvoicesFilters(prevState => ({
            ...prevState,
            [filterName]: value ? invoicesFilters[filterName] : [],
        }));
    };

    const isFilterChecked = (filterName: keyof PaidInvoicesFilters, filterValue: string): boolean => {
        return selectedInvoicesFilters[filterName].includes(filterValue);
    };

    const isAllFiltersChecked = (filterName: keyof PaidInvoicesFilters): boolean => {
        return invoicesFilters[filterName].length === selectedInvoicesFilters[filterName].length;
    };

    const clearFilters = () => {
        setSelectedInvoicesFilters(invoicesFilters);
        setOpenedFilter("");
        setFilterSearch({
            paymentDate: "",
            paidBy: "",
            paymentMethod: "",
            invoiceNumber: "",
            custPoNumber: "",
            invoiceDate: "",
            dueDate: "",
            amountDue: "",
            amountPaid: "",
            shipTo: "",
            balanceDueDisplay: "",
            termsDiscountDate: "",
        });
    };

    const shouldShowClearFilterButton = () => {
        return (
            JSON.stringify(invoicesFilters) !== JSON.stringify(selectedInvoicesFilters) ||
            areObjectValuesNotEmpty(filterSearch)
        );
    };
    
    return (
        <LoadingOverlay data-test-selector="paidInvoices" loading={isLoading}>
            <StyledWrapper>
                <Typography variant="h2" as="h1" data-test-selector="PageTitle" {...style.pageTitle}>
                    {capitalize(billTo?.customerName ? `${billTo?.customerName} - ` : "")}
                    {pageTitle}
                </Typography>
            </StyledWrapper>
            {!isLoading && !filteredInvoices.length && !errorMessage && (
                <Notify
                    data-test-selector="PaidInvoices_EmptyInvoices"
                    body={siteMessage("Custom_PaidInvoices_EmptyInvoices")}
                    messageType="danger"
                    {...style.notifyMessage.wrapper}
                />
            )}
            {errorMessage &&
                <Notify
                    data-test-selector="PaidInvoices_ErrorMessage"
                    body={errorMessage}
                    messageType="danger"
                />
            }
            {shouldShowClearFilterButton() &&
                <Button onClick={clearFilters} {...style.filters.clearAll}>{translate("Clear All Filters")}</Button>
            }
            {invoices?.length > 0 && (
                <>
                    <StyledWrapper data-test-selector="paidInvoices_table" {...(openedFilter ? style.tableWrapperWithFilter : style.tableWrapper)}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Payment Date")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "paymentDate")}
                                            {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("paymentDate") &&
                                            <PaidInvoiceFilter
                                                filterName="paymentDate"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Paid By")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "paidBy")}
                                            {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("paidBy") &&
                                            <PaidInvoiceFilter
                                                filterName="paidBy"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Payment Method")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "paymentMethod")}
                                            {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("paymentMethod") &&
                                            <PaidInvoiceFilter
                                                filterName="paymentMethod"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Invoice #")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "invoiceNumber")}
                                            {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("invoiceNumber") &&
                                            <PaidInvoiceFilter
                                                filterName="invoiceNumber"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("PO #")}
                                        <Clickable
                                            onClick={(event: React.MouseEvent) => toggleFilter(event, "custPoNumber")}
                                            {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("custPoNumber") &&
                                            <PaidInvoiceFilter
                                                filterName="custPoNumber"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Invoice Date")}
                                        <Clickable
                                        onClick={(event: React.MouseEvent) => toggleFilter(event, "invoiceDate")}
                                        {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("invoiceDate") &&
                                            <PaidInvoiceFilter
                                                filterName="invoiceDate"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Due Date")}
                                        <Clickable
                                        onClick={(event: React.MouseEvent) => toggleFilter(event, "dueDate")}
                                        {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("dueDate") &&
                                            <PaidInvoiceFilter
                                                filterName="dueDate"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight {...style.balanceDueCell}>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Amount Due")}
                                        <Clickable
                                        onClick={(event: React.MouseEvent) => toggleFilter(event, "amountDue")}
                                        {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("amountDue") &&
                                            <PaidInvoiceFilter
                                                filterName="amountDue"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                                <DataTableHeader tight>
                                    <StyledWrapper {...style.tableHeaderWrapper}>
                                        {translate("Amount Paid")}
                                        <Clickable
                                        onClick={(event: React.MouseEvent) => toggleFilter(event, "amountPaid")}
                                        {...style.filters.filterButton}>
                                            <Icon src={Filter} {...style.filters.filterIcon} />
                                        </Clickable>
                                        {isOpenedFilter("amountPaid") &&
                                            <PaidInvoiceFilter
                                                filterName="amountPaid"
                                                filterSearch={filterSearch}
                                                onChangeFilterSearch={onChangeFilterSearch}
                                                onChangeAllSelectFilterHandler={onChangeAllSelectFilterHandler}
                                                onChangeFilterHandler={onChangeFilterHandler}
                                                isAllFiltersChecked={isAllFiltersChecked}
                                                isFilterChecked={isFilterChecked}
                                                getFilters={getFilters}
                                                toggleFilter={toggleFilter}
                                                style={style} />
                                        }
                                    </StyledWrapper>
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {filteredInvoices.map(
                                    ({
                                        paymentDate,
                                        paidBy,
                                        paymentMethod,
                                        invoiceNumber,
                                        custPoNumber,
                                        invoiceDate,
                                        dueDate,
                                        amountDueDisplay,
                                        amountPaidDisplay,
                                        exist
                                    }, index) => (
                                        <DataTableRow key={index}>
                                            <DataTableCell {...style.columnCell}>{paymentDate}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{paidBy}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{paymentMethod}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>
                                            {exist ? (
                                                    <Link
                                                        href={`/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`}
                                                        data-test-selector="PaidInvoice_invoiceLine_number"
                                                        {...style.invoiceNumber}
                                                    >
                                                        {invoiceNumber}
                                                    </Link>
                                                ) : (
                                                    invoiceNumber
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...style.columnCell}>{custPoNumber}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{invoiceDate}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{dueDate}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{amountDueDisplay}</DataTableCell>
                                            <DataTableCell {...style.columnCell}>{amountPaidDisplay}</DataTableCell>
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </>
            )}
        </LoadingOverlay>
    );
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Paid Invoices Widget",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PaidInvoicesWidget),
    definition,
};

export default widgetModule;
